/**
 * Shared terminal configuration module.
 *
 * Centralizes theme definitions, addon loading, and settings-to-options mapping
 * so that both TerminalPanel (desktop) and MobileTerminal (mobile) share identical
 * configuration without duplication.
 */
import type { Terminal, ITerminalOptions, ITheme, IDisposable } from '@xterm/xterm'
import { WebglAddon } from '@xterm/addon-webgl'

// ---------------------------------------------------------------------------
// Theme definitions
// ---------------------------------------------------------------------------

export const DARK_THEME: ITheme = {
  background: '#1a1a1a',
  foreground: '#e0e0e0',
  cursor: '#e0e0e0',
  cursorAccent: '#1a1a1a',
  selectionBackground: '#4a4a4a',
  black: '#1a1a1a',
  red: '#ff5555',
  green: '#50fa7b',
  yellow: '#f1fa8c',
  blue: '#bd93f9',
  magenta: '#ff79c6',
  cyan: '#8be9fd',
  white: '#e0e0e0',
  brightBlack: '#6e6e6e',
  brightRed: '#ff6e6e',
  brightGreen: '#69ff94',
  brightYellow: '#ffffa5',
  brightBlue: '#d6acff',
  brightMagenta: '#ff92df',
  brightCyan: '#a4ffff',
  brightWhite: '#ffffff',
}

export const LIGHT_THEME: ITheme = {
  background: '#ffffff',
  foreground: '#383a42',
  cursor: '#383a42',
  cursorAccent: '#ffffff',
  selectionBackground: '#bfceff',
  black: '#383a42',
  red: '#e45649',
  green: '#50a14f',
  yellow: '#c18401',
  blue: '#4078f2',
  magenta: '#a626a4',
  cyan: '#0184bc',
  white: '#fafafa',
  brightBlack: '#a0a1a7',
  brightRed: '#e06c75',
  brightGreen: '#98c379',
  brightYellow: '#e5c07b',
  brightBlue: '#61afef',
  brightMagenta: '#c678dd',
  brightCyan: '#56b6c2',
  brightWhite: '#ffffff',
}

// ---------------------------------------------------------------------------
// Theme helpers
// ---------------------------------------------------------------------------

/**
 * Returns the ITheme object for the given theme preference string.
 * Defaults to dark if the preference is unrecognized.
 */
export function getTerminalTheme(themePreference: string): ITheme {
  return themePreference === 'light' ? LIGHT_THEME : DARK_THEME
}

/**
 * Returns the background color string for the terminal container div so that
 * the surrounding div matches the terminal canvas background.
 */
export function getBackgroundColor(themePreference: string): string {
  return themePreference === 'light' ? '#ffffff' : '#1a1a1a'
}

// ---------------------------------------------------------------------------
// Cursor style mapping
// ---------------------------------------------------------------------------

/**
 * Maps a settings cursor_style string to xterm ITerminalOptions cursorStyle.
 * Defaults to 'block' for unrecognized values.
 */
export function mapCursorStyle(style: string): 'block' | 'underline' | 'bar' {
  if (style === 'underline' || style === 'bar') return style
  return 'block'
}

// ---------------------------------------------------------------------------
// Terminal options factory
// ---------------------------------------------------------------------------

interface TerminalSettingsInput {
  fontSize?: number
  theme?: string
  scrollbackLines?: number
  cursorStyle?: string
}

/**
 * Creates the full ITerminalOptions object from user settings.
 * Used in both desktop and mobile terminal constructors.
 */
export function createTerminalOptions(settings: TerminalSettingsInput): ITerminalOptions {
  return {
    cursorBlink: true,
    fontSize: settings.fontSize ?? 14,
    fontFamily: 'Menlo, Monaco, "Courier New", monospace',
    scrollback: settings.scrollbackLines ?? 10000,
    cursorStyle: mapCursorStyle(settings.cursorStyle ?? 'block'),
    theme: getTerminalTheme(settings.theme ?? 'dark'),
    allowProposedApi: true,
  }
}

// ---------------------------------------------------------------------------
// Bell handler
// ---------------------------------------------------------------------------

/**
 * Registers a bell handler that flashes the document title when a BEL
 * character (\x07) is received by the terminal.
 *
 * Rate-limited to prevent rapid flashing from scroll-edge bells or
 * tmux copy-mode invalid key bells.
 *
 * Returns the IDisposable from terminal.onBell for cleanup.
 */
export function setupBellHandler(
  terminal: Terminal,
  options?: { flashDuration?: number; minInterval?: number },
): IDisposable {
  const duration = options?.flashDuration ?? 2000
  const minInterval = options?.minInterval ?? 3000 // Don't flash more than once per 3s
  let lastBellTime = 0

  return terminal.onBell(() => {
    const now = Date.now()
    if (now - lastBellTime < minInterval) return // Rate-limit
    lastBellTime = now

    const originalTitle = document.title
    document.title = '\u{1F514} ' + originalTitle
    setTimeout(() => {
      document.title = originalTitle
    }, duration)
  })
}

// ---------------------------------------------------------------------------
// WebGL addon loader
// ---------------------------------------------------------------------------

/**
 * Loads the WebGL renderer addon with context-loss handling.
 *
 * On context loss the addon is disposed and the terminal falls back to the
 * DOM renderer automatically. If WebGL cannot be initialized at all (no
 * context available), a warning is logged and null is returned.
 *
 * Returns the addon instance (or null) and a dispose function for cleanup.
 */
export function loadWebGLAddon(
  terminal: Terminal,
): { addon: WebglAddon | null; dispose: () => void } {
  try {
    const addon = new WebglAddon()
    addon.onContextLoss(() => {
      addon.dispose()
    })
    terminal.loadAddon(addon)
    return {
      addon,
      dispose: () => {
        try {
          addon.dispose()
        } catch {
          // Already disposed — safe to ignore
        }
      },
    }
  } catch (e) {
    console.warn('WebGL renderer not available, using DOM renderer', e)
    return { addon: null, dispose: () => {} }
  }
}
